import { z } from "zod"
import { LISTING_STATUSES, ROLES, RUN_ORIGINS, TRACKED_SKU_SOURCES } from "../status"

// ── Primitives ─────────────────────────────────────────────────────────────
//
// `isoDate` and `jsonRecord` are exported for the sibling modules in this folder
// only — `index.ts` deliberately does not re-export them, so they stay as
// file-private as they were in the single `schemas.ts`.

export const isoDate = z.string().min(1)
/** The one YYYY-MM-DD validator. Exported so every calendar-day field in the
 *  monorepo shares this regex + real-date refinement instead of re-deriving it.
 *  The refinement runs on malformed input too (a failed `regex` check does not
 *  stop it), so it must REJECT rather than throw on anything unparseable —
 *  `toISOString()` on an invalid Date raises a RangeError, which would escape a
 *  `safeParse` as an unhandled error. */
export const isoCalendarDay = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD")
  .refine((value) => {
    const [year, month, day] = value.split("-").map(Number)
    if (year === undefined || month === undefined || day === undefined) {
      return false
    }
    const date = new Date(Date.UTC(year, month - 1, day))
    if (Number.isNaN(date.getTime())) {
      return false
    }
    return date.toISOString().slice(0, 10) === value
  }, "expected a real calendar date")

// Shapes mirror the Postgres columns. JSON-encoded `meta` is parsed at the API
// boundary so callers always see a typed object, not a string.
export const jsonRecord = z.record(z.string(), z.unknown())

export const listingStatusSchema = z.enum(LISTING_STATUSES)
export const trackedSkuSourceSchema = z.enum(TRACKED_SKU_SOURCES)
export const runOriginSchema = z.enum(RUN_ORIGINS)
export const roleSchema = z.enum(ROLES)

// The product master category a run targets. A run is started for one of these
// (clothing / nutrition / equipment); kept in sync with the backend
// `apps/api/src/modules/products/master-categories.ts` enum.
export const masterCategorySchema = z.enum(["nutrition", "clothing", "equipment"])
export type MasterCategory = z.infer<typeof masterCategorySchema>
